import type { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { BaseQueryFn, MutationDefinition } from '@reduxjs/toolkit/query';

import type { DesignGroup } from '../types/storeReviewOrderStep.types';
import {
  getStoreReviewItemMinimumQuantity,
  getStoreReviewQuotedItemPrice,
  needsStoreReviewBreakpointPricing,
} from './storeReviewPricing.utils';
import {
  applyStoreCartQuoteToCartItem,
  fetchStoreCartItemQuote,
  hasUsableStoreCartPriceList,
  mergeStoreCartQuotePriceList,
  type IStoreCartQuoteOptions,
} from '@/modules/Cart/StoreCart/helpers/storeCartQuote.utils';
import { isFreshPrintsApparelBrand } from '@/modules/Cart/StoreCart/helpers/storeCartPricing.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { IQuoteResponse, QuoterPayload } from '@/common/interfaces/quoter.interface';

type GetQuoteMutationFunction = MutationTrigger<
  MutationDefinition<
    { data: QuoterPayload },
    BaseQueryFn<{ url: string; method: string }>,
    never,
    IQuoteResponse[],
    'fetchQuoterApi'
  >
>;

const resolveStoreReviewQuoteBrand = (
  group: DesignGroup,
  item: ICartItemDetail
): string | undefined =>
  group.items.some((groupItem) => isFreshPrintsApparelBrand(groupItem.apparelBrand))
    ? 'Fresh Prints'
    : item.apparelBrand;

/** Match store cart: siblings share the selected minimum before designGroupQuantity is sent to quoter. */
export const buildStoreReviewQuoteContextItems = (
  group: DesignGroup,
  allItems: ICartItemDetail[],
  quantity: number
): ICartItemDetail[] => {
  const groupItemIds = new Set(group.items.map((groupItem) => groupItem.id));

  return allItems.map((item) =>
    groupItemIds.has(item.id) ? { ...item, quantity, minQuant: quantity } : item
  );
};

export const areStoreReviewItemsQuotedAtQuantity = (
  items: ICartItemDetail[],
  quantity: number
): boolean =>
  items.every((item) => {
    if (typeof item.designId !== 'number') {
      return true;
    }

    const itemMinimum = Number(item.minQuant ?? item.quantity ?? 0);
    const priceList = item.priceList ?? [];
    const hasQuotedUnitPrice =
      typeof item.price === 'number' && item.price > 0 && itemMinimum === quantity;

    if (!hasQuotedUnitPrice || priceList.length === 0) {
      return false;
    }

    // Single-tier quotes are valid; multi-tier lists must have distinct volume prices.
    if (priceList.length === 1) {
      return true;
    }

    return hasUsableStoreCartPriceList(priceList, item.apparelBrand);
  });

export const areStoreReviewItemsQuotedAtTheirMinimums = (items: ICartItemDetail[]): boolean =>
  items.every((item) => {
    if (typeof item.designId !== 'number') {
      return true;
    }

    const itemMinimum = getStoreReviewItemMinimumQuantity(item);

    if (!itemMinimum) {
      return false;
    }

    return getStoreReviewQuotedItemPrice(itemMinimum, item) != null;
  });

export const buildStoreReviewItemsQuoteStateKey = (group: DesignGroup): string =>
  group.items.map((item) => `${item.id}:${getStoreReviewItemMinimumQuantity(item)}`).join('|');

export const quoteStoreReviewDesignGroupItemsAtTheirMinimums = async (
  group: DesignGroup,
  allItems: ICartItemDetail[],
  getQuote: GetQuoteMutationFunction,
  quoteOptions: IStoreCartQuoteOptions = {}
): Promise<ICartItemDetail[]> => {
  const quotedItems = await Promise.all(
    group.items.map(async (item) => {
      const quantity = getStoreReviewItemMinimumQuantity(item);

      if (!quantity || typeof item.designId !== 'number') {
        return item;
      }

      if (!quoteOptions.forceRequote && getStoreReviewQuotedItemPrice(quantity, item) != null) {
        return item;
      }

      const quote = await fetchStoreCartItemQuote(
        { ...item, quantity },
        allItems,
        quantity,
        getQuote,
        quoteOptions
      );

      if (!quote) {
        return item;
      }

      return applyStoreCartQuoteToCartItem(
        item,
        quantity,
        quote,
        resolveStoreReviewQuoteBrand(group, item)
      );
    })
  );

  return quotedItems.map((quotedItem) => {
    const apparelPrice = quotedItem.apparelPrice ?? 0;

    if (
      quotedItem.strikedPricePerUnit &&
      apparelPrice > 0 &&
      quotedItem.strikedPricePerUnit === apparelPrice
    ) {
      return { ...quotedItem, strikedPricePerUnit: 0 };
    }

    return quotedItem;
  });
};

export const quoteStoreReviewDesignGroupBreakpointPriceLists = async (
  group: DesignGroup,
  allItems: ICartItemDetail[],
  quantities: number[],
  getQuote: GetQuoteMutationFunction,
  quoteOptions: IStoreCartQuoteOptions = {}
): Promise<ICartItemDetail[]> => {
  const quotedItems = await Promise.all(
    group.items.map(async (item) => {
      if (
        typeof item.designId !== 'number' ||
        !needsStoreReviewBreakpointPricing(item, quantities)
      ) {
        return item;
      }

      const quantity = getStoreReviewItemMinimumQuantity(item) || quantities[0];

      if (!quantity) {
        return item;
      }

      const quote = await fetchStoreCartItemQuote(
        { ...item, quantity },
        allItems,
        quantity,
        getQuote,
        quoteOptions
      );

      if (!quote) {
        return item;
      }

      const brand = resolveStoreReviewQuoteBrand(group, item);
      const mergedPriceList = mergeStoreCartQuotePriceList(item.priceList, quote.priceList, brand);
      const quotedItem = applyStoreCartQuoteToCartItem(item, quantity, quote, brand);
      const resolvedPrice =
        typeof item.price === 'number' && item.price > 0 ? item.price : quotedItem.price;

      return {
        ...quotedItem,
        priceList: mergedPriceList,
        price: resolvedPrice,
        strikedPricePerUnit:
          quotedItem.strikedPricePerUnit === item.apparelPrice ? 0 : quotedItem.strikedPricePerUnit,
      };
    })
  );

  return quotedItems;
};

export const quoteStoreReviewDesignGroupItems = async (
  group: DesignGroup,
  allItems: ICartItemDetail[],
  quantity: number,
  getQuote: GetQuoteMutationFunction,
  quoteOptions: IStoreCartQuoteOptions = {}
): Promise<ICartItemDetail[]> => {
  const quoteContextItems = buildStoreReviewQuoteContextItems(group, allItems, quantity);

  const quotedItems = await Promise.all(
    group.items.map(async (item) => {
      if (typeof item.designId !== 'number') {
        return item;
      }

      const quote = await fetchStoreCartItemQuote(
        item,
        quoteContextItems,
        quantity,
        getQuote,
        quoteOptions
      );

      if (!quote) {
        return item;
      }

      return applyStoreCartQuoteToCartItem(
        item,
        quantity,
        quote,
        resolveStoreReviewQuoteBrand(group, item)
      );
    })
  );

  return quotedItems.map((quotedItem) => {
    const apparelPrice = quotedItem.apparelPrice ?? 0;

    if (
      quotedItem.strikedPricePerUnit &&
      apparelPrice > 0 &&
      quotedItem.strikedPricePerUnit === apparelPrice
    ) {
      return { ...quotedItem, strikedPricePerUnit: 0 };
    }

    return quotedItem;
  });
};

export const applyQuotedItemsToDesignGroups = (
  designGroups: DesignGroup[],
  groupIndex: number,
  quotedItems: ICartItemDetail[]
): DesignGroup[] =>
  designGroups.map((group, index) => {
    if (index !== groupIndex) {
      return group;
    }

    return {
      ...group,
      items: group.items.map((item) => {
        const quotedItem = quotedItems.find((candidate) => candidate.id === item.id);

        return quotedItem ?? item;
      }),
    };
  });

export const buildStoreReviewQuoteRequestKey = (
  groupIndex: number,
  quantity: number,
  group: DesignGroup
): string => `${groupIndex}:${quantity}:${group.items.map((item) => item.id).join(',')}`;
